import React, { useState } from 'react';

import { kebabCase } from 'lodash';
import { useQueryClient } from 'react-query';

import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';

import { useProjectTreeStore } from 'store/projectContract.store';

import { useMutationTrackUnTrackItems } from 'api/mutations/trackUnTrackItems.mutation';

import { CustomizedTooltip } from 'components/common/CustomizedTooltip/CustomizedTooltip';
import { StarIcon } from 'components/common/Icons/common/StarIcon/StarIcon';
import { LoaderSpinner } from 'components/common/LodaerSpinner/LoaderSpinner';

import classes from './trackedUntrackedAction.module.scss';

interface ITrackedUntrackedActionProps {
    taskVersionHashCode: string;
    isTracked?: boolean;
    invalidateQueriesKey: string[];
    dataTrackId?: string;
}

export const TrackedUntrackedAction = ({
    isTracked = false,
    taskVersionHashCode,
    invalidateQueriesKey,
    dataTrackId = '',
}: ITrackedUntrackedActionProps) => {
    const { handleSnackBar } = useCustomSnackBar();
    const { mutate, isLoading } = useMutationTrackUnTrackItems();
    const queryClient = useQueryClient();

    const [isLoadingInvalidateQueries, setIsLoadingInvalidateQueries] = useState<boolean>(false);
    const { contract } = useProjectTreeStore();

    const handleTrackActivity = (e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();

        setIsLoadingInvalidateQueries(true);

        mutate(
            {
                taskVersionHashCode: taskVersionHashCode,
                toTrack: !isTracked,
                contractId: contract?.id,
            },
            {
                onSuccess: () => {
                    return Promise.all(invalidateQueriesKey.map((key) => queryClient.invalidateQueries(key))).then(
                        () => {
                            setIsLoadingInvalidateQueries(false);
                            isTracked
                                ? handleSnackBar('Removed From Track Activities', 'success')
                                : handleSnackBar('Added To Track Activities', 'success');
                        },
                    );
                },
                onError: (error) => {
                    DEFAULT_ERROR({ error, handleError: handleSnackBar });
                    setIsLoadingInvalidateQueries(false);
                },
            },
        );
    };

    return (
        <div className={classes.trackedUntrackedActionContainer}>
            {isLoading || isLoadingInvalidateQueries ? (
                <LoaderSpinner />
            ) : (
                <CustomizedTooltip
                    tooltipContent={isTracked ? 'Tracked Activity' : 'Click to set as Tracked Activity'}
                    triggerElement={
                        <StarIcon
                            isActive={isTracked}
                            onClick={handleTrackActivity}
                            data-track-id={kebabCase(
                                `Tracked-Untracked-Action-${
                                    isTracked ? 'Tracked-Activity' : 'un-Tracked-Activity'
                                } ${dataTrackId}`,
                            )}
                        />
                    }
                />
            )}
        </div>
    );
};
